import { useDataProvider, useGetList } from "ra-core";
import { useEffect, useState } from "react";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import {
  cleanFilters,
  type Broadcast,
  type BroadcastDraft,
  type BroadcastFilters,
  type BroadcastSegment,
} from "./broadcasts";

export const useBroadcasts = () => {
  const {
    data = [],
    isPending,
    refetch,
  } = useGetList<Broadcast>(
    "gula_broadcasts_summary",
    {
      pagination: { page: 1, perPage: 300 },
      sort: { field: "created_at", order: "DESC" },
    },
    { refetchInterval: 60_000 },
  );
  return { broadcasts: data, isPending, refetch };
};

/** People a broadcast would reach now (debounced while the filters change). */
export const useAudienceCount = (
  segment: BroadcastSegment,
  filters: BroadcastFilters,
) => {
  const [count, setCount] = useState<number | null>(null);
  const key = JSON.stringify([segment, cleanFilters(filters)]);
  useEffect(() => {
    let cancelled = false;
    setCount(null);
    const timer = setTimeout(async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "gula_broadcast_count",
        { p_segment: segment, p_filters: cleanFilters(filters) },
      );
      if (!cancelled) setCount(error ? null : Number(data));
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return count;
};

/** Zones that exist in the pipeline, for the filter. */
export const useZones = () => {
  const { data = [] } = useGetList<{ id: number; zone?: string | null }>(
    "gula_clients",
    {
      pagination: { page: 1, perPage: 2000 },
      sort: { field: "id", order: "ASC" },
    },
  );
  return [...new Set(data.map((row) => row.zone ?? "none"))].sort();
};

export const invokeBroadcast = async (
  action: "submit" | "cancel" | "refresh",
  broadcastId?: Broadcast["id"],
) => {
  const { data, error } = await getSupabaseClient().functions.invoke(
    "whatsapp_send",
    {
      method: "POST",
      body: {
        action: `broadcast_${action}`,
        broadcast_id: broadcastId == null ? undefined : Number(broadcastId),
      },
    },
  );
  if (error) {
    let message: string | undefined;
    try {
      message = (await (error as { context?: Response }).context?.json())
        ?.message;
    } catch {
      // keep the generic message
    }
    throw new Error(message ?? "No se ha podido completar");
  }
  return data;
};

/** Saves a draft (creates it the first time) and returns its id. */
export const useSaveBroadcast = () => {
  const dataProvider = useDataProvider();
  return async (draft: BroadcastDraft, meId: unknown) => {
    const data = {
      title: draft.title.trim(),
      segment: draft.segment,
      filters: cleanFilters(draft.filters),
      material_id: draft.material_id == null ? null : Number(draft.material_id),
      body: draft.body.trim(),
      scheduled_at: draft.scheduled_at
        ? new Date(draft.scheduled_at).toISOString()
        : null,
    };
    if (draft.id != null) {
      await dataProvider.update("gula_broadcasts", {
        id: draft.id,
        data,
        previousData: { id: draft.id },
      });
      return draft.id;
    }
    const { data: created } = await dataProvider.create("gula_broadcasts", {
      data: { ...data, created_by: meId ?? null },
    });
    return created.id;
  };
};
